export interface RenderingReference {
	token: string;
	referenceType: "file" | "image";
	fileId: string;
	timestamp?: string | null;
	kind: "image" | "file" | "video_frame" | "unknown";
	mimeType?: string | null;
	signedUrl?: string | null;
	resolved: boolean;
}

export interface RenderingMetadata {
	mode: "raw" | "rendered";
	references: RenderingReference[];
}

const MARKDOWN_IMAGE_WITH_TARGET_REGEX = /!\[([^\]]*)\]\(\s*(<?[^)\s>]+>?)(?:\s+"[^"]*")?\s*\)/g;
const MARKDOWN_LINK_WITH_TARGET_REGEX = /(?<!!)\[([^\]]+)\]\(\s*(<?[^)\s>]+>?)(?:\s+"[^"]*")?\s*\)/g;

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeMarkdownTarget(target: string): string {
	const withoutWrappers = target.trim().replace(/^<|>$/g, "");
	return withoutWrappers.replace(/&amp;/g, "&");
}

function getDisplayReplacement(reference: RenderingReference, signedUrl: string): string {
	if (reference.kind === "image" || reference.kind === "video_frame") {
		return `![Image](${signedUrl})`;
	}
	return `[Download](${signedUrl})`;
}

function getResolvedReferences(rendering?: RenderingMetadata | null): RenderingReference[] {
	if (!rendering?.references?.length) return [];
	return rendering.references.filter((reference) => !!reference.signedUrl);
}

function cleanTableNbsp(markdown: string): string {
	return markdown.split('\n').map(line => {
		if (!line.trimStart().startsWith('|')) return line;
		if (/^\s*\|[\s\-:|]+\|\s*$/.test(line)) return line;
		return line.split('|').map(cell =>
			cell.trim() === '&nbsp;' ? cell.replace('&nbsp;', '') : cell
		).join('|');
	}).join('\n');
}

/**
 * Converts tokenized markdown content into editor-friendly markdown with signed URLs.
 * This is intended for raw-mode editing payloads.
 */
export function applyRenderingReferencesToMarkdown(
	markdown: string | null | undefined,
	rendering?: RenderingMetadata | null
): string {
	if (!markdown) return "";

	const resolvedReferences = getResolvedReferences(rendering);
	if (resolvedReferences.length === 0) return markdown;

	let transformed = markdown;
	for (const reference of resolvedReferences) {
		const signedUrl = reference.signedUrl!;
		const escapedToken = escapeRegExp(reference.token);

		const imageTokenRegex = new RegExp(`!\\[([^\\]]*)\\]\\(\\s*${escapedToken}(?:\\s+"[^"]*")?\\s*\\)`, "g");
		transformed = transformed.replace(imageTokenRegex, (_match, altText: string) => {
			return `![${altText}](${signedUrl})`;
		});

		const linkTokenRegex = new RegExp(`(?<!!)\\[([^\\]]+)\\]\\(\\s*${escapedToken}(?:\\s+"[^"]*")?\\s*\\)`, "g");
		transformed = transformed.replace(linkTokenRegex, (_match, linkText: string) => {
			return `[${linkText}](${signedUrl})`;
		});

		const htmlSrcTokenRegex = new RegExp(`(\\bsrc\\s*=\\s*["'])${escapedToken}(["'])`, "gi");
		transformed = transformed.replace(htmlSrcTokenRegex, (_m: string, pre: string, post: string) => `${pre}${signedUrl}${post}`);

		const htmlHrefTokenRegex = new RegExp(`(\\bhref\\s*=\\s*["'])${escapedToken}(["'])`, "gi");
		transformed = transformed.replace(htmlHrefTokenRegex, (_m: string, pre: string, post: string) => `${pre}${signedUrl}${post}`);

		const standaloneTokenRegex = new RegExp(escapedToken, "g");
		transformed = transformed.replace(standaloneTokenRegex, () => getDisplayReplacement(reference, signedUrl));
	}

	return transformed;
}

/**
 * Converts editor markdown that contains signed URLs back to tokenized references for persistence.
 */
export function restoreRenderingReferencesFromMarkdown(
	markdown: string | null | undefined,
	rendering?: RenderingMetadata | null
): string {
	if (!markdown) return "";

	const cleanedMarkdown = cleanTableNbsp(markdown);

	const resolvedReferences = getResolvedReferences(rendering);
	if (resolvedReferences.length === 0) return cleanedMarkdown;

	const urlToToken = new Map<string, string>();
	for (const reference of resolvedReferences) {
		const signedUrl = normalizeMarkdownTarget(reference.signedUrl!);
		urlToToken.set(signedUrl, reference.token);
	}

	let restored = cleanedMarkdown.replace(MARKDOWN_IMAGE_WITH_TARGET_REGEX, (match, altText: string, target: string) => {
		const normalizedTarget = normalizeMarkdownTarget(target);
		const token = urlToToken.get(normalizedTarget);
		if (!token) return match;
		return `![${altText}](${token})`;
	});

	restored = restored.replace(MARKDOWN_LINK_WITH_TARGET_REGEX, (match, linkText: string, target: string) => {
		const normalizedTarget = normalizeMarkdownTarget(target);
		const token = urlToToken.get(normalizedTarget);
		if (!token) return match;
		return `[${linkText}](${token})`;
	});

	for (const reference of resolvedReferences) {
		const signedUrl = reference.signedUrl!;
		const escapedSignedUrl = escapeRegExp(signedUrl);
		const escapedHtmlSignedUrl = escapeRegExp(signedUrl.replace(/&/g, "&amp;"));
		const targetToken = reference.token;

		restored = restored.replace(new RegExp(escapedSignedUrl, "g"), () => targetToken);
		restored = restored.replace(new RegExp(escapedHtmlSignedUrl, "g"), () => targetToken);
	}

	return restored;
}
